import {
  Loader,
  Select,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton
} from "@marketplace/ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdOutlineInfo } from "react-icons/md";

import { useGetIndustryCategoryListQuery } from "../../hooks/queries/use-get-industry-category-list-query";
import { useGetProductFeeTierListQuery } from "../../hooks/queries/use-get-product-fee-tier-list-query";
import { useGetProductTypeListQuery } from "../../hooks/queries/use-get-product-type-list-query";
import type { ProductFormProps } from "../../types";
import { PicturesUpload } from "../pictures-upload";
import { ProductFormSectionCard } from "../product-form-section-card";
import { ProductFormApiPackage } from "./product-form-api-package";
import { ProductFormDescription } from "./product-form-description";
import { ProductFormIndustrySelect } from "./product-form-industry-select";
import { ProductFormPackages } from "./product-form-packages";
import { ProductFormRecurringOrder } from "./product-form-recurring-order";

export const ProductForm: React.FC<ProductFormProps> = ({
  formMethods,
  productCategory
}) => {
  const { t: tProduct } = useTranslation("product");

  const { data, isLoading: productTypeLoading } = useGetProductTypeListQuery();
  const { data: feeTierData, isLoading: feeTierLoading } =
    useGetProductFeeTierListQuery();
  const { data: industryCategoryData, isLoading: industryCategoryLoading } =
    useGetIndustryCategoryListQuery();

  const productTypeSelectData = useMemo(() => {
    return (
      data?.data.map((item) => ({
        label: item.name,
        value: item.id.toString()
      })) ?? []
    );
  }, [data]);

  const industryCategorySelectData = useMemo(() => {
    return (
      industryCategoryData?.data.map((item) => ({
        value: item.slug_name,
        label: item.name
      })) ?? []
    );
  }, [industryCategoryData]);

  const {
    register,
    formState: { errors },
    control,
    trigger
  } = formMethods;

  // Debounced validation to prevent lag
  const validationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedValidation = useCallback(() => {
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    validationTimerRef.current = setTimeout(() => {
      trigger("productDescription");
    }, 500); // Validate 500ms after user stops typing
  }, [trigger]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col gap-y-6">
      {/* Product Detail Section */}
      <ProductFormSectionCard
        id="product-detail"
        title={tProduct("addProductPage.form.legends.productDetails")}
        showPromoPill
      >
        <div className="flex flex-col gap-y-6">
          <TextInput
            {...register("productName")}
            withAsterisk
            label={tProduct("addProductPage.form.labels.productName")}
            placeholder={tProduct(
              "addProductPage.form.placeholders.productName"
            )}
            error={errors.productName?.message?.toString()}
          />
          <Controller
            name="productType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={productTypeSelectData}
                label={tProduct("addProductPage.form.labels.productType")}
                placeholder={tProduct(
                  "addProductPage.form.placeholders.productType"
                )}
                error={errors.productType?.message?.toString()}
                onChange={(value) => field.onChange(value)}
                rightSection={productTypeLoading && <Loader size="sm" />}
                allowDeselect={false}
                withAsterisk
              />
            )}
          />
          <Controller
            name="industryCategory"
            control={control}
            render={({ field }) => (
              <ProductFormIndustrySelect
                data={industryCategorySelectData}
                value={field.value || []}
                onChange={field.onChange}
                label={tProduct("addProductPage.form.labels.industryCategory")}
                placeholder={tProduct(
                  "addProductPage.form.placeholders.industryCategory"
                )}
                error={errors.industryCategory?.message?.toString()}
                isLoading={industryCategoryLoading}
              />
            )}
          />
          <Controller
            name="productService"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={[
                  { label: "Quota", value: "quota" },
                  { label: "Period", value: "period" }
                ]}
                label={
                  <div className="flex items-center">
                    {tProduct("addProductPage.form.labels.productService")}
                    <span className="text-red-500">*</span>
                    <Tooltip
                      label={
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-1">
                            <Text type="heading" size="ss" weight="semibold">
                              Quota:{" "}
                              {tProduct(
                                "addProductPage.form.tooltips.productService.quota"
                              )}
                            </Text>
                          </div>
                          <div className="flex items-center gap-1">
                            <Text type="heading" size="ss" weight="semibold">
                              Period:{" "}
                              {tProduct(
                                "addProductPage.form.tooltips.productService.period"
                              )}
                            </Text>
                          </div>
                        </div>
                      }
                      position="top"
                      withArrow
                      multiline
                      classNames={{
                        tooltip:
                          "!rounded-lg !bg-[#454545] p-[14px] w-[276px] !text-white !text-h-ss !font-semibold"
                      }}
                    >
                      <UnstyledButton className="flex items-center justify-center order-last ml-1">
                        <MdOutlineInfo size={16} />
                      </UnstyledButton>
                    </Tooltip>
                  </div>
                }
                placeholder={tProduct(
                  "addProductPage.form.placeholders.productService"
                )}
                error={errors.productService?.message?.toString()}
                onChange={(value) => field.onChange(value)}
                allowDeselect={false}
              />
            )}
          />
          <Controller
            name="productDescription"
            control={control}
            render={({ field }) => (
              <ProductFormDescription
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  // Debounced validation to prevent lag while typing
                  debouncedValidation();
                }}
                label={tProduct(
                  "addProductPage.form.labels.productDescription"
                )}
                placeholder={tProduct(
                  "addProductPage.form.placeholders.productDescription"
                )}
                withAsterisk
                error={errors.productDescription?.message?.toString()}
              />
            )}
          />
        </div>
      </ProductFormSectionCard>

      {/* Media Section */}
      <ProductFormSectionCard
        id="media"
        title={tProduct("addProductPage.form.legends.media")}
      >
        <PicturesUpload formMethods={formMethods} />
      </ProductFormSectionCard>

      {/* Recurring Order Section */}
      <ProductFormRecurringOrder formMethods={formMethods} />

      {/* Product Packages Section */}
      <ProductFormSectionCard
        id="product-packages"
        title={tProduct("addProductPage.form.legends.productPackages")}
      >
        <Text className="mb-3 text-fjord-grey-500">
          {tProduct("addProductPage.form.legends.packageList")}
        </Text>
        <div className="p-2 rounded-lg bg-mischka-100">
          <ProductFormPackages
            formMethods={formMethods}
            feeTiers={feeTierData?.data ?? []}
            isLoadingFeeTiers={feeTierLoading}
          />
        </div>
      </ProductFormSectionCard>

      {/* Terms & Condition Section */}
      <ProductFormSectionCard
        id="terms-condition"
        title={tProduct("addProductPage.form.legends.tnc")}
      >
        <div className="flex flex-col gap-y-6">
          <Textarea
            {...register("tncCheckout")}
            withAsterisk
            label={tProduct("addProductPage.form.labels.tncCheckout")}
            placeholder={tProduct("addProductPage.form.placeholders.tnc")}
            classNames={{ input: "h-36" }}
            error={errors.tncCheckout?.message?.toString()}
          />
          <Textarea
            {...register("tncTrial")}
            label={tProduct("addProductPage.form.labels.tncTrial")}
            placeholder={tProduct("addProductPage.form.placeholders.tncTrial")}
            classNames={{ input: "h-36" }}
            error={errors.tncTrial?.message?.toString()}
          />
        </div>
      </ProductFormSectionCard>

      {/* API Package Section */}
      {productCategory === "api" && (
        <ProductFormSectionCard
          id="api-package"
          title={
            <div className="flex items-center gap-1">
              <Text
                type="heading"
                size="l"
                weight="bold"
                className="text-fjord-grey-400"
              >
                API Package
              </Text>
              <Tooltip
                label={tProduct(
                  "addProductPage.form.tooltips.apiDocumentation"
                )}
                position="top"
                withArrow
                multiline
                classNames={{
                  tooltip:
                    "!rounded-lg !bg-[#454545] p-[14px] w-[276px] !text-white !text-h-ss !font-semibold"
                }}
              >
                <UnstyledButton className="flex items-center justify-center">
                  <MdOutlineInfo size={16} />
                </UnstyledButton>
              </Tooltip>
            </div>
          }
        >
          <ProductFormApiPackage formMethods={formMethods} />
        </ProductFormSectionCard>
      )}
    </div>
  );
};
